/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable react/no-unused-prop-types */
/* eslint-disable react/no-array-index-key */
/* eslint-disable no-alert */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import useSWRInfinite from 'swr/infinite';

import { CanvasBoardGenerator } from '@components/canvas-board-generator';
import * as Styles from '@components/modals/styles';
import { ThumbnailBoard } from '@components/thumbnail-board';
import { ONE_MINUTE_MILLIS } from '@constants';
import { IBoard } from '@interface/board';
import { IToken } from '@interface/token';
import { uploadFile } from '@lib/ipfs/adapters/infura';
import { canvasToBlob } from '@utils/canvas';
import { tokensFetcher } from '@utils/fetcher';
import { Node } from '@utils/ipfs';
import { customFloor } from '@utils/math';
import { getSmallestImageBoardPreview } from '@utils/token';
import { AlertDialog, Box, Button, Step, Text } from '@versumstudios/ui';

import * as PrivateStyles from './styles';

const MediaWrapper = dynamic(() => import('@components/media'), { ssr: false });
const Grid = dynamic(() => import('@components/grid'), { ssr: false });

export interface IBoardCoverEdit {
  open?: boolean;
  onOpenChange?: (value: boolean) => void;
  callback?: (success: boolean, result?: any) => void;
  board?: IBoard;
}

const limit = 30;

export const BoardCoverEdit: FC<IBoardCoverEdit> = ({ open, callback, board }) => {
  const [confirming, setConfirming] = useState(false);
  const generator = useRef<any>(null);
  // which id's are selected and in which order
  const [selection, setSelection] = useState<string[]>([]);
  const startTime = customFloor(Date.now(), ONE_MINUTE_MILLIS);

  const { data, error, size, setSize, isValidating } = useSWRInfinite(
    (i: number, previousPageData: any) => {
      if (previousPageData && !previousPageData.length) return null;
      if (board?.id) {
        return `/api/public/boards/${board.id}/tokens?&offset=${(i + 0) * limit}&maxtime=${startTime}`;
      }
      return null;
    },
    tokensFetcher,
    {
      revalidateIfStale: false,
      revalidateOnMount: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshInterval: 0,
    }
  );
  const items: IToken[] = data ? [].concat(...data) : [];
  const isLoadingInitialData = !data && !error;
  const isLoadingMore = isLoadingInitialData || (size > 0 && data && typeof data[size - 1] === 'undefined');
  const isEmpty = data && data.length === 0;
  const isReachingEnd = isEmpty || (data && data[data.length - 1]?.length < limit);
  const isRefreshing = isValidating && data && data.length === size;

  const handleConfirm = async () => {
    if (board) {
      setConfirming(true);

      const blob = await canvasToBlob(generator.current!);
      if (blob) {
        // upload to IPFS
        const cid = await uploadFile(blob as File, true, false);

        // warm up IPFS gateways, and fail silently
        fetch(`/api/ipfs/warmup/${cid}`).catch((e) => {
          console.log('error on warmup', e);
        });

        // callback
        if (callback) callback(true, { uri: `ipfs://${cid}` });
      }
    }
  };

  const handleCancel = () => {
    if (callback) callback(false);
  };

  const toggleSelection = (selectedId: string) => {
    const addOrRemove = selection.indexOf(selectedId) === -1;

    if (addOrRemove) {
      // add to (only up to 4 items)
      if (selection.length < 4) {
        setSelection((previousSelection) => [...previousSelection, selectedId]);
      }
    } else {
      // remove from
      setSelection((previousSelection) => previousSelection.filter((e: string) => e !== selectedId));
    }
  };

  const preview = selection
    .map((id) => {
      const found = items.filter((i) => i.id === id);
      if (found) {
        return getSmallestImageBoardPreview(found[0], Node.INFURA);
      }
      return null;
    })
    .map((e: any) => e.src);

  // todo: design mobile
  // design desktop
  // save image and progress on ipfs
  return (
    <AlertDialog
      title="Change Cover"
      description="You can select 4 images to represent your board."
      open={open}
      large
      // onConfirm={handleConfirm}
      onCancel={handleCancel}
      // onOpenChange={onOpenChange}
    >
      <Styles.Content>
        <Box css={{ position: 'relative', flexDirection: 'column-reverse', '@bp1': { flexDirection: 'row' } }}>
          <Grid columns={4} space="8px">
            {items.map((token: IToken) => {
              const isSelected = selection.indexOf(token.id) !== -1;

              return (
                <PrivateStyles.Content
                  key={`cover-${token.id}`}
                  onClick={() => toggleSelection(token.id)}
                  selected={isSelected}
                >
                  <PrivateStyles.Overlay />
                  {isSelected && (
                    <PrivateStyles.Checkmark>
                      <i>done</i>
                    </PrivateStyles.Checkmark>
                  )}

                  <MediaWrapper token={token} inDetailView={false} labels={false} />
                </PrivateStyles.Content>
              );
            })}
          </Grid>

          <Box
            css={{
              display: 'block',
              width: '100%',
              height: '100%',

              '@bp1': {
                position: 'sticky',
                top: 0,
                alignSelf: 'flex-start',
                width: 300,
                height: 300,
                marginLeft: 16,
              },
            }}
          >
            {false && <ThumbnailBoard images={preview} />}
            <CanvasBoardGenerator ref={generator} images={preview} />
            <Box css={{ flexDirection: 'column', padding: '$space$4' }}>
              <Text unset css={{ textAlign: 'left' }} ellipsis>
                <strong>{board?.title}</strong>
              </Text>
            </Box>
          </Box>
        </Box>

        {!isReachingEnd && (
          <Box css={{ margin: '$1 0' }} horizontal>
            {isLoadingMore ? (
              <Step progress />
            ) : (
              <Button onClick={() => setSize(size + 1)} variant="secondary" disabled={isRefreshing}>
                Load more
              </Button>
            )}
          </Box>
        )}

        <Styles.Buttons>
          <Button
            onClick={handleConfirm}
            variant="primary"
            full
            css={{ marginBottom: '$3', position: 'relative' }}
            disabled={confirming}
          >
            Generate Cover
            {confirming && (
              <Box
                css={{
                  position: 'absolute',
                  top: '50%',
                  right: '$space$4',
                  transform: 'translateY(-50%)',
                }}
                vertical
              >
                <Step progress small />
              </Box>
            )}
          </Button>
        </Styles.Buttons>
      </Styles.Content>
    </AlertDialog>
  );
};
